/*
 * Cartão de produto Bracci (blocos bracci-card-*).
 *
 * 1. Troca de acabamento no próprio cartão: o clique numa bolinha do bloco
 *    "Bracci: Acabamentos" troca foto, selos, referência, título, preço, link do
 *    cartão e o botão adicionar, com os dados do JSON [data-bc-finish-data].
 * 2. Botão adicionar: põe no carrinho o acabamento escolhido e avisa o carrinho do
 *    Fabric (ícone e gaveta) com o mesmo evento do botão nativo (CartLinesUpdateEvent),
 *    seguindo assets/product-form.js.
 *
 * Os eventos são delegados no document, então continuam funcionando quando o editor
 * de temas recarrega uma seção ou quando a coleção carrega mais produtos.
 */

if (!window.BracciCard) {
  // Dados de acabamento por cartão. Quando o editor recarrega um bloco, o cartão é um
  // elemento novo e os dados são lidos de novo.
  const cards = new WeakMap();

  /* ---------- Utilidades ---------- */

  function cardOf(element) {
    return element.closest('product-card') || element.closest('.product-card');
  }

  function finishesOf(card) {
    if (cards.has(card)) return cards.get(card);

    let list = [];
    const script = card.querySelector('[data-bc-finish-data]');
    if (script) {
      try {
        list = JSON.parse(script.textContent);
      } catch (error) {
        console.warn('[bracci-card] JSON de acabamentos inválido', error);
      }
    }
    cards.set(card, list);
    return list;
  }

  function sized(url, width) {
    if (!url) return '';
    const absolute = url.startsWith('//') ? `https:${url}` : url;
    const parsed = new URL(absolute, window.location.origin);
    parsed.searchParams.set('width', String(width));
    return url.startsWith('//') ? parsed.href.replace(/^https:/, '') : parsed.href;
  }

  function srcset(url) {
    return [240, 360, 480, 640, 800, 1000].map((width) => `${sized(url, width)} ${width}w`).join(', ');
  }

  function handleize(text) {
    return String(text)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
  }

  // Mesmo formato do filtro money do Shopify (shop.money_format).
  function formatMoney(cents, format) {
    const value = Number(cents) || 0;
    const template = format || 'R$ {{amount_with_comma_separator}}';

    function number(amount, decimals, thousands, decimal) {
      const fixed = (amount / 100).toFixed(decimals);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    }

    return template.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount':
          return number(value, 2, ',', '.');
        case 'amount_no_decimals':
          return number(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return number(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return number(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return number(value, 2, "'", '.');
        default:
          return number(value, 2, '.', ',');
      }
    });
  }

  /* ---------- Preço (mesmo HTML de snippets/bracci-card-price-lines.liquid) ---------- */

  function renderPrice(element, finish) {
    const data = element.dataset;
    const format = data.moneyFormat;
    const price = Number(finish.price) || 0;
    const compare = Number(finish.compare) || 0;
    const pix = Number(data.pix) || 0;
    const installmentMin = Number(data.installmentMin) || 0;

    let count = Number(data.installments) || 1;
    if (installmentMin > 0 && price > 0) count = Math.min(count, Math.floor(price / installmentMin));
    if (count < 1) count = 1;

    const hasPix = data.showPix === 'true' && pix > 0;
    const main = hasPix ? Math.round((price * (100 - pix)) / 100) : price;

    let html = '';
    if (data.showCompare === 'true' && compare > price) {
      const prefix = data.comparePrefix ? `${escapeHtml(data.comparePrefix)} ` : '';
      html += `<span class="bc-price__compare">${prefix}<s>${formatMoney(compare, format)}</s></span>`;
    }

    html += `<span class="bc-price__main"><span class="bc-price__value">${formatMoney(main, format)}</span>`;
    if (hasPix && data.pixLabel) html += `<span class="bc-price__pix">${escapeHtml(data.pixLabel)}</span>`;
    html += '</span>';

    if (data.showInstallments === 'true' && count > 1 && data.installmentsText) {
      const text = escapeHtml(data.installmentsText)
        .replace('[preco]', formatMoney(price, format))
        .replace('[parcelas]', String(count))
        .replace('[valor]', formatMoney(Math.floor(price / count), format));
      html += `<span class="bc-price__installments">${text}</span>`;
    }

    element.innerHTML = html;
  }

  /* ---------- Troca de acabamento ---------- */

  function applyFinish(card, finish) {
    card.querySelectorAll('[data-bc-swatch]').forEach((swatch) => {
      const active = swatch.dataset.bcSwatch === String(finish.id);
      swatch.classList.toggle('is-active', active);
      swatch.setAttribute('aria-pressed', String(active));
    });

    // Foto e selos
    card.querySelectorAll('[data-bc-image]').forEach((block) => {
      const main = block.querySelector('.bc-image__img--main');
      if (main && finish.image) {
        main.srcset = srcset(finish.image);
        main.src = sized(finish.image, 800);
        main.alt = finish.alt || finish.title;
      }

      const second = block.querySelector('.bc-image__img--second');
      if (second) {
        if (finish.second) {
          second.srcset = srcset(finish.second);
          second.src = sized(finish.second, 800);
          second.hidden = false;
        } else {
          second.hidden = true;
        }
      }

      const soldout = block.querySelector('[data-bc-badge="soldout"]');
      if (soldout) soldout.hidden = finish.available;

      const discount = block.querySelector('[data-bc-badge="discount"]');
      if (discount) {
        const pct = finish.compare > finish.price ? Math.round(((finish.compare - finish.price) * 100) / finish.compare) : 0;
        discount.hidden = pct < 1;
        if (pct >= 1) discount.textContent = (block.dataset.discountTemplate || '-[pct]%').replace('[pct]', String(pct));
      }

      const tagBadge = block.querySelector('[data-bc-badge="tag"]');
      if (tagBadge) {
        const wanted = block.dataset.tagBadge;
        tagBadge.hidden = !wanted || !(finish.tags || []).some((tag) => handleize(tag) === wanted);
      }
    });

    // Referência
    card.querySelectorAll('[data-bc-sku]').forEach((block) => {
      const sku = finish.sku || (block.dataset.variantFallback === 'true' ? finish.variantSku : '');
      const value = block.querySelector('[data-bc-sku-value]');
      if (value) value.textContent = sku || '';
      block.hidden = !sku;
    });

    // Título
    card.querySelectorAll('[data-bc-title]').forEach((block) => {
      block.textContent = finish.title;
    });

    // Preço
    card.querySelectorAll('[data-bc-price]').forEach((block) => renderPrice(block, finish));

    // Botão adicionar
    card.querySelectorAll('[data-bc-add]').forEach((block) => {
      const button = block.querySelector('[data-bc-add-button]');
      const options = block.querySelector('[data-bc-add-options]');
      const label = block.querySelector('[data-bc-add-label]');
      if (!button) return;

      button.dataset.variantId = String(finish.variant);
      // Pré-venda (app Globo): o plano de venda vai junto para o carrinho
      button.dataset.sellingPlan = finish.plan ? String(finish.plan) : '';
      button.classList.remove('is-added', 'is-error', 'is-loading');

      const hasOptions = Number(finish.variants) > 1;
      if (options) {
        options.hidden = !hasOptions;
        options.href = finish.url;
      }
      button.hidden = hasOptions;
      button.disabled = !finish.available;
      if (label) {
        if (!finish.available) label.textContent = block.dataset.labelSoldout;
        else if (finish.presale && block.dataset.labelPresale) label.textContent = block.dataset.labelPresale;
        else label.textContent = block.dataset.labelAdd;
      }
    });

    // Link do cartão inteiro (Fabric) e transição para a página do produto
    const link = card.querySelector('.product-card__link');
    if (link) {
      link.href = finish.url;
      link.setAttribute('aria-label', finish.title);
    }
    if (card.dataset.featuredMediaUrl && finish.image) card.dataset.featuredMediaUrl = sized(finish.image, 500);
  }

  function onSwatchClick(event) {
    const swatch = event.target.closest('[data-bc-swatch]');
    if (!swatch) return;

    const card = cardOf(swatch);
    if (!card) return;

    event.preventDefault();
    const finish = finishesOf(card).find((item) => String(item.id) === swatch.dataset.bcSwatch);
    if (finish) applyFinish(card, finish);
  }

  // Pré-carrega a foto do acabamento ao passar o mouse, para a troca ser imediata.
  function onSwatchHover(event) {
    const swatch = event.target.closest?.('[data-bc-swatch]');
    if (!swatch || swatch.dataset.bcPreloaded) return;

    const card = cardOf(swatch);
    const finish = card && finishesOf(card).find((item) => String(item.id) === swatch.dataset.bcSwatch);
    if (!finish?.image) return;

    swatch.dataset.bcPreloaded = 'true';
    const image = new Image();
    image.srcset = srcset(finish.image);
    image.sizes = '(min-width: 990px) 25vw, 50vw';
    image.src = sized(finish.image, 800);
  }

  /* ---------- Botão adicionar ---------- */

  async function refreshCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
    }

    const cartUrl = window.Theme?.routes?.cart_url || '/cart';
    const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    return response.json();
  }

  function flash(button, block, state, text, duration) {
    const label = button.querySelector('[data-bc-add-label]');
    button.classList.remove('is-loading');
    button.classList.add(state);
    if (label) label.textContent = text;

    clearTimeout(button.bcTimer);
    button.bcTimer = setTimeout(() => {
      button.classList.remove(state);
      if (label) label.textContent = button.dataset.sellingPlan && block.dataset.labelPresale ? block.dataset.labelPresale : block.dataset.labelAdd;
    }, duration);
  }

  async function onAddClick(event) {
    const button = event.target.closest('[data-bc-add-button]');
    if (!button || button.disabled || button.classList.contains('is-loading')) return;

    event.preventDefault();
    const block = button.closest('[data-bc-add]');
    const card = cardOf(button);
    const variantId = button.dataset.variantId;
    const sellingPlan = button.dataset.sellingPlan;
    if (!variantId) return;

    button.classList.remove('is-added', 'is-error');
    button.classList.add('is-loading');

    let events = null;
    try {
      events = await import('@shopify/events');
    } catch (error) {
      console.warn('[bracci-card] @shopify/events indisponível; o carrinho só atualiza ao recarregar.', error);
    }

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);

    const deferred = events?.CartLinesUpdateEvent.createPromise();
    if (events && deferred) {
      button.dispatchEvent(
        new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          promise: deferred.promise,
        })
      );
    }

    const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            sellingPlan
              ? { id: Number(variantId), quantity: 1, selling_plan: Number(sellingPlan) }
              : { id: Number(variantId), quantity: 1 },
          ],
          sections: sectionIds.join(','),
        }),
      }).then((result) => result.json());

      const detailBase = {
        source: 'bracci-card',
        sourceId: card?.id || '',
        itemCount: 1,
        productId: card?.dataset.productId,
      };

      if (response.status) {
        if (events) {
          button.dispatchEvent(
            new events.CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );
        }
        if (deferred) {
          refreshCart()
            .then((cart) =>
              deferred.resolve({
                cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
                detail: { ...detailBase, items: cart.items, didError: true },
              })
            )
            .catch(deferred.reject);
        }
        flash(button, block, 'is-error', response.description || block.dataset.labelError, 2600);
        return;
      }

      if (deferred) {
        const cart = await refreshCart();
        deferred.resolve({
          cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detailBase, items: cart.items, sections: response.sections, didError: false },
        });
      }
      flash(button, block, 'is-added', block.dataset.labelAdded, 1600);
    } catch (error) {
      console.error('[bracci-card]', error);
      deferred?.reject(error);
      flash(button, block, 'is-error', block.dataset.labelError, 2600);
    }
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    onSwatchClick(event);
    onAddClick(event);
  });

  document.addEventListener('pointerover', onSwatchHover, { passive: true });
  document.addEventListener('focusin', onSwatchHover);

  window.BracciCard = { applyFinish, formatMoney };
}
